/** V2PitchPage — /pitch/:token
 *  Private, tokenised, noindex pitch experience.
 *  First deployed target: 2TN Fabrication — "2TN Operations OS" (client: Daniel Visser).
 *  Design matches the source PDF (2TN_Operations_OS.pdf) page-for-page:
 *  Montserrat only, near-black layered backgrounds, gold accent system,
 *  diagonal blade motifs, dashboard mockups cropped from the PDF itself.
 *  NO quote section visible per client's explicit rule.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import V2AchPitchPage from './V2AchPitchPage';
import V2BasePitchPage from './V2BasePitchPage';
import V2HPCIPitchPage from './V2HPCIPitchPage';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Design tokens (extracted from source PDF) ────────────────────────
const T = {
  bg0:      '#101010',
  bg1:      '#141414',
  bg2:      '#1c1c1c',
  border:   '#2a2a2a',
  white:    '#ffffff',
  ink2:     '#8a8a8a',
  ink3:     '#5c5c5c',
  gold:     '#c9a24a',
  goldHi:   '#f2e5bc',
  goldMid:  '#8a6d3b',
  goldLo:   '#7a5e30',
};
// Signature gold gradient — dark bronze → pale gold
const GOLD_GRAD = 'linear-gradient(90deg, #7a5e30 0%, #8a6d3b 28%, #c9a24a 60%, #f2e5bc 100%)';

// ── Kindra brand tokens (bookend frame around the 2TN experience) ─────
const K = {
  navy:     '#100838',
  navy2:    '#160c44',
  navyDeep: '#0a0424',
  icy:      '#DCF0F5',
  icyMute:  'rgba(220,240,245,0.72)',
  icyDim:   'rgba(220,240,245,0.5)',
};
const SPECTRUM = 'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)';

const ASSETS = '/pitch-assets/2tn';

// ── Global styles ────────────────────────────────────────────────────
const PageStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
    * { box-sizing: border-box; }
    html, body { background: ${T.bg0}; margin: 0; }
    .p2tn { font-family: 'Montserrat', -apple-system, system-ui, sans-serif; color: ${T.white}; }
    .p2tn h1, .p2tn h2, .p2tn h3, .p2tn h4 { font-family: 'Montserrat', sans-serif; margin: 0; }

    @keyframes p2tn-in { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes p2tn-slide { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
    @keyframes p2tn-lift  { from { opacity: 0; transform: translateY(40px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes p2tn-bar   { from { width: 0; } to { width: var(--w, 100%); } }

    .p2tn-reveal { opacity: 0; will-change: opacity, transform; }
    .p2tn-reveal.is-in { animation: p2tn-in 720ms cubic-bezier(0.22,1,0.36,1) both; }
    .p2tn-reveal.lift.is-in { animation-name: p2tn-lift; }
    .p2tn-reveal.slide.is-in { animation-name: p2tn-slide; }
    .p2tn-reveal.d1.is-in { animation-delay: 80ms; }
    .p2tn-reveal.d2.is-in { animation-delay: 160ms; }
    .p2tn-reveal.d3.is-in { animation-delay: 240ms; }
    .p2tn-reveal.d4.is-in { animation-delay: 320ms; }
    .p2tn-reveal.d5.is-in { animation-delay: 400ms; }
    .p2tn-reveal.d6.is-in { animation-delay: 480ms; }

    /* Gold gradient text via background-clip */
    .gold-text {
      background: ${GOLD_GRAD};
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
    }

    .p2tn-eyebrow {
      font-family: 'Montserrat', sans-serif;
      font-weight: 600;
      font-size: 12px;
      letter-spacing: 0.18em;
      text-transform: uppercase;
    }

    .p2tn-btn {
      font-family: 'Montserrat', sans-serif;
      font-weight: 700;
      letter-spacing: 0.06em;
      padding: 16px 30px;
      border-radius: 4px;
      border: 1px solid ${T.border};
      cursor: pointer;
      transition: transform 220ms cubic-bezier(0.22,1,0.36,1),
                  background 220ms ease,
                  border-color 220ms ease,
                  box-shadow 240ms ease;
      font-size: 13px;
    }
    .p2tn-btn.primary {
      background: ${T.gold}; color: ${T.bg0}; border-color: ${T.gold};
    }
    .p2tn-btn.primary:hover { background: ${T.goldHi}; box-shadow: 0 18px 38px rgba(201,162,74,0.28); transform: translateY(-2px); }
    .p2tn-btn.ghost { background: transparent; color: ${T.white}; }
    .p2tn-btn.ghost:hover { border-color: ${T.gold}; color: ${T.gold}; }

    .p2tn-card {
      background: ${T.bg2};
      border: 1px solid ${T.border};
      border-radius: 4px;
      transition: border-color 240ms ease, transform 260ms cubic-bezier(0.22,1,0.36,1);
    }
    .p2tn-card:hover { border-color: rgba(201,162,74,0.4); transform: translateY(-2px); }

    /* Left gold rule that appears before the number in each leak/cost card */
    .p2tn-rule {
      display: inline-block; width: 3px; height: 14px; background: ${T.gold};
      vertical-align: middle; margin-right: 10px; border-radius: 1px;
    }

    /* Diagonal blade divider (small) */
    .p2tn-blade-sm {
      background-image: url('${ASSETS}/blade-corner.png');
      background-size: contain; background-position: right center; background-repeat: no-repeat;
    }

    .p2tn-progress { position: fixed; left: 0; top: 0; height: 3px; background: ${T.gold}; z-index: 80; transition: width 120ms linear; }

    .p2tn-mockup {
      display: block; width: 100%; border-radius: 4px; border: 1px solid ${T.border};
      background: ${T.bg1};
    }

    .p2tn-mockup-btn { transition: box-shadow 260ms ease, border-color 260ms ease, transform 260ms ease; }
    .p2tn-mockup-btn:hover { border-color: ${T.gold}; box-shadow: 0 40px 90px rgba(201,162,74,0.14), 0 40px 100px rgba(0,0,0,0.5); }
    .p2tn-mockup-btn:hover .p2tn-enlarge-hint { background: ${T.gold}; color: ${T.bg0}; border-color: ${T.gold}; }

    .p2tn-enlarge-hint {
      position: absolute; right: 14px; top: 14px;
      display: inline-flex; align-items: center; gap: 8px;
      padding: 8px 12px; border-radius: 999px;
      background: rgba(16,16,16,0.72); backdrop-filter: blur(8px);
      border: 1px solid ${T.border};
      color: ${T.gold};
      font-family: 'Montserrat', sans-serif; font-weight: 600; font-size: 11px;
      letter-spacing: 0.14em; text-transform: uppercase;
      transition: background 220ms ease, color 220ms ease, border-color 220ms ease;
      pointer-events: none;
    }
    .p2tn-enlarge-hint svg { display: block; }

    /* Lightbox */
    .p2tn-lb-overlay {
      position: fixed; inset: 0; z-index: 100;
      background: rgba(0,0,0,0.94);
      display: grid; grid-template-rows: auto 1fr auto;
      animation: p2tn-in 200ms ease-out;
    }
    .p2tn-lb-bar {
      display: flex; align-items: center; justify-content: space-between; gap: 20px;
      padding: 14px 22px; border-bottom: 1px solid ${T.border};
    }
    .p2tn-lb-close {
      background: transparent; border: 1px solid ${T.border}; color: ${T.white};
      width: 40px; height: 40px; border-radius: 999px; cursor: pointer;
      font-family: 'Montserrat', sans-serif; font-size: 18px; line-height: 1;
      transition: background 200ms, border-color 200ms;
    }
    .p2tn-lb-close:hover { background: ${T.gold}; color: ${T.bg0}; border-color: ${T.gold}; }
    .p2tn-lb-scroll {
      overflow: auto; padding: 20px;
      display: grid; place-items: center;
      -webkit-overflow-scrolling: touch;
      touch-action: pinch-zoom;
    }
    .p2tn-lb-scroll img {
      display: block; max-width: 100%; height: auto;
      user-select: none; -webkit-user-drag: none;
    }

    .p2tn-num { font-variant-numeric: tabular-nums; font-feature-settings: "tnum"; }

    @media (prefers-reduced-motion: reduce) {
      .p2tn-reveal, .p2tn-reveal.is-in { animation: none !important; opacity: 1 !important; transform: none !important; }
      .k-aurora-a, .k-aurora-b, .k-aurora-c { animation: none !important; }
    }

    /* ── Kindra bookend styles ────────────────────────────────────── */
    .k-section {
      position: relative; overflow: hidden;
      background: radial-gradient(1200px 700px at 20% 10%, ${K.navy2} 0%, ${K.navy} 55%, ${K.navyDeep} 100%);
      color: ${K.icy};
    }
    .k-aurora {
      position: absolute; inset: 0; pointer-events: none; overflow: hidden;
    }
    .k-aurora-a, .k-aurora-b, .k-aurora-c {
      position: absolute; border-radius: 50%;
      filter: blur(70px); opacity: 0.35; mix-blend-mode: screen;
    }
    .k-aurora-a { width: 720px; height: 720px; top: -180px; right: -140px; background: #9156C4; animation: k-drift-a 22s ease-in-out infinite alternate; }
    .k-aurora-b { width: 620px; height: 620px; bottom: -160px; left: -120px; background: #1FB6C1; opacity: 0.28; animation: k-drift-b 26s ease-in-out infinite alternate; }
    .k-aurora-c { width: 460px; height: 460px; top: 34%; left: 42%; background: #EB258D; opacity: 0.20; animation: k-drift-c 30s ease-in-out infinite alternate; }
    @keyframes k-drift-a { from { transform: translate(0,0) scale(1); } to { transform: translate(-80px, 60px) scale(1.08); } }
    @keyframes k-drift-b { from { transform: translate(0,0) scale(1); } to { transform: translate(70px, -40px) scale(1.10); } }
    @keyframes k-drift-c { from { transform: translate(0,0) scale(1); } to { transform: translate(50px, 30px) scale(1.06); } }

    .k-spectrum-text {
      background: ${SPECTRUM};
      -webkit-background-clip: text; background-clip: text;
      -webkit-text-fill-color: transparent; color: transparent;
    }
    .k-spectrum-bar { display: block; width: 120px; height: 3px; background: ${SPECTRUM}; border-radius: 2px; }
    .k-hairline { display: block; width: 100%; height: 1px; background: ${SPECTRUM}; opacity: 0.85; }

    .k-cta {
      font-family: 'Montserrat', sans-serif; font-weight: 700; letter-spacing: 0.05em;
      padding: 16px 30px; border-radius: 999px; cursor: pointer; font-size: 13px;
      text-decoration: none; display: inline-flex; align-items: center; gap: 10px;
      transition: transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 240ms ease, background 220ms ease, border-color 220ms ease, color 220ms ease;
      border: 1px solid transparent;
    }
    .k-cta.primary { background: ${SPECTRUM}; color: #0F0824; }
    .k-cta.primary:hover { transform: translateY(-2px); box-shadow: 0 20px 40px rgba(145,86,196,0.35); }
    .k-cta.ghost { background: transparent; color: ${K.icy}; border-color: rgba(220,240,245,0.28); }
    .k-cta.ghost:hover { border-color: ${K.icy}; transform: translateY(-2px); }

    /* Transition band: fades Kindra navy into 2TN black at the seam */
    .k-to-t2n {
      position: relative; height: 200px;
      background: linear-gradient(180deg, ${K.navyDeep} 0%, ${K.navy2} 20%, rgba(16,16,16,0.72) 65%, ${T.bg0} 100%);
    }
    .k-to-t2n::after {
      content: ''; position: absolute; left: 0; right: 0; top: 50%;
      height: 1px; background: ${SPECTRUM}; opacity: 0.35;
    }
    .t2n-to-k {
      position: relative; height: 200px;
      background: linear-gradient(180deg, ${T.bg0} 0%, rgba(16,16,16,0.85) 30%, ${K.navy2} 70%, ${K.navyDeep} 100%);
    }
    .t2n-to-k::after {
      content: ''; position: absolute; left: 0; right: 0; top: 50%;
      height: 1px; background: ${GOLD_GRAD}; opacity: 0.5;
    }

    @keyframes k-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
    .k-scroll-cue { animation: k-bob 2.4s ease-in-out infinite; }
  `}</style>
);

// ── Hooks ────────────────────────────────────────────────────────────
function useReveal(threshold = 0.18) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } });
    }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, cls: seen ? 'is-in' : '' };
}

function useCountUp(target, seen, duration = 1400) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!seen) return;
    const start = performance.now();
    const end = Number(target) || 0;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.round(end * eased));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, seen, duration]);
  return v;
}

function useSectionTracker(sectionKey, index, onSeen) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.3) { onSeen(sectionKey, index); io.disconnect(); }
      });
    }, { threshold: [0.3, 0.5] });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [sectionKey, index, onSeen]);
  return ref;
}

// ── Building blocks ──────────────────────────────────────────────────
function RevealItem({ children, className = '', delay = 0 }) {
  const r = useReveal();
  const dCls = delay ? `d${((delay - 1) % 6) + 1}` : '';
  return (
    <div ref={r.ref} className={`p2tn-reveal ${className} ${dCls} ${r.cls}`}>{children}</div>
  );
}

function HeaderLogo({ eyebrow }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <img src={`${ASSETS}/logo-header.png`} alt="2TN Fabrication" style={{ height: 44, display: 'block' }} />
      {eyebrow && (
        <>
          <span aria-hidden style={{ display: 'inline-block', width: 1, height: 22, background: T.border }} />
          <span className="p2tn-eyebrow gold-text">{eyebrow}</span>
        </>
      )}
    </div>
  );
}

function SectionHead({ eyebrow, title, sub, style }) {
  return (
    <div style={{ maxWidth: 1240, margin: '0 auto', ...style }}>
      <HeaderLogo eyebrow={eyebrow} />
      <h2 style={{
        fontFamily: 'Montserrat', fontWeight: 700, letterSpacing: '-0.02em',
        fontSize: 'clamp(38px, 5.4vw, 62px)', lineHeight: 1.05,
        color: T.white, marginTop: 90,
      }}>
        {title}
      </h2>
      {sub && (
        <p style={{
          fontFamily: 'Montserrat', fontWeight: 400, fontSize: 17, color: T.ink2,
          marginTop: 22, maxWidth: 680, lineHeight: 1.6,
        }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function Section({ id, sectionKey, index, onSeen, children, style, corner = true }) {
  const ref = useSectionTracker(sectionKey, index, onSeen);
  return (
    <section
      id={id} ref={ref} data-section={sectionKey}
      style={{
        position: 'relative',
        padding: 'clamp(84px, 9vw, 130px) clamp(24px, 6vw, 96px)',
        background: T.bg0, overflow: 'hidden',
        ...style,
      }}
    >
      {corner && (
        <img src={`${ASSETS}/blade-corner.png`} alt="" aria-hidden
          style={{ position: 'absolute', right: 0, top: 0, height: 'clamp(140px, 18vw, 260px)', pointerEvents: 'none', opacity: 0.9 }}
        />
      )}
      <div style={{ position: 'relative' }}>{children}</div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SECTIONS
// ─────────────────────────────────────────────────────────────────────

function CoverSection({ onSeen, prepared_for, business_name }) {
  const r = useReveal();
  return (
    <Section id="s-cover" sectionKey="cover" index={0} onSeen={onSeen}
      style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', paddingTop: 'clamp(80px, 9vw, 120px)' }}
      corner={false}
    >
      {/* Hero right-side blade */}
      <img src={`${ASSETS}/blade-hero.png`} alt="" aria-hidden
        style={{
          position: 'absolute', right: 0, top: '10%', height: '80%',
          maxWidth: '46vw', objectFit: 'contain', objectPosition: 'right center',
          pointerEvents: 'none', opacity: 0.95,
        }}
      />
      <div ref={r.ref} className={`p2tn-reveal ${r.cls}`} style={{ maxWidth: 1240, margin: '0 auto', width: '100%', position: 'relative' }}>
        <img src={`${ASSETS}/logo-hero.png`} alt="2TN Fabrication"
          style={{ display: 'block', width: 'clamp(240px, 26vw, 380px)', height: 'auto' }} />

        <div className="p2tn-eyebrow gold-text" style={{ marginTop: 40, fontSize: 12 }}>
          THE 2TN OPERATIONS OS
        </div>

        <h1 style={{
          fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.03em',
          fontSize: 'clamp(56px, 8vw, 108px)', lineHeight: 1.02,
          color: T.white, marginTop: 40,
        }}>
          One screen.
        </h1>
        <h1 style={{
          fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.03em',
          fontSize: 'clamp(56px, 8vw, 108px)', lineHeight: 1.02,
          color: T.white, marginTop: 20,
        }}>
          The whole shop.
        </h1>

        <p style={{
          fontFamily: 'Montserrat', fontWeight: 400, fontSize: 'clamp(16px, 1.5vw, 19px)',
          color: T.ink2, marginTop: 44, maxWidth: 620, lineHeight: 1.65,
        }}>
          Quoting, jobs, the crew, the money &mdash; and an assistant that reads all of it, so the day doesn&rsquo;t have to start at five.
        </p>

        <div aria-hidden style={{ marginTop: 60, width: 160, height: 3, background: GOLD_GRAD, borderRadius: 2 }} />

        <div style={{ marginTop: 22, fontFamily: 'Montserrat', fontWeight: 400, fontSize: 13, color: T.ink3, letterSpacing: '0.02em' }}>
          Prepared for {prepared_for || 'Daniel Visser'} &nbsp;&middot;&nbsp; {business_name || '2TN Fabrication'} &nbsp;&middot;&nbsp; July 2026
        </div>
      </div>
    </Section>
  );
}

function LeaksSection({ onSeen }) {
  const items = [
    { n: '01', t: 'The 5am start', b: `You're in before the crew just to get one clear hour on quotes and scheduling. That's not focus time — that's the day already behind you.` },
    { n: '02', t: 'Quotes typed twice', b: `Every line item retyped or pasted into Xero. Every job is custom, so there's no catalogue to lean on.` },
    { n: '03', t: 'The list that gets pasted', b: 'Jobs live on a weekly list. Greens deleted, the rest pasted into next week. It works — until something falls off it.' },
    { n: '04', t: 'Nobody chases the quote', b: `No nudge when one goes quiet. You're tracking opened-versus-unopened in your head.` },
    { n: '05', t: 'The win rate is blind', b: '439 won, 114 lost. Good numbers. But nothing warns you which quote is about to go cold.' },
    { n: '06', t: 'Tasks live in the air', b: `Scott, Kai, Kate and Ty get told. Nothing tracks it. Nothing tells you who's underwater.` },
  ];
  return (
    <Section id="s-leaks" sectionKey="leaks" index={1} onSeen={onSeen}>
      <SectionHead eyebrow="THE LEAKS" title="What we found in the shop"
        sub="Not a technology problem. A double-handling problem. Six of them." />
      <div style={{ maxWidth: 1240, margin: '58px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
        {items.map((it, i) => (
          <RevealItem key={it.n} className="lift" delay={i + 1}>
            <div className="p2tn-card" style={{ padding: '28px 30px 32px', minHeight: 200 }}>
              <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>
                <span className="p2tn-rule" />{it.n}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 22, color: T.white, marginTop: 14, letterSpacing: '-0.01em' }}>{it.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 12, lineHeight: 1.6 }}>{it.b}</div>
            </div>
          </RevealItem>
        ))}
      </div>
    </Section>
  );
}

function MetricCard({ prefix, val, suffix, sub, detail, delay }) {
  const rr = useReveal();
  const seen = !!rr.cls;
  const n = useCountUp(val, seen, 1400);
  return (
    <div ref={rr.ref} className={`p2tn-reveal lift ${rr.cls}`} style={{ animationDelay: `${delay * 80}ms` }}>
      <div className="p2tn-card" style={{ padding: '30px 28px 32px', minHeight: 180 }}>
        <div className="gold-text p2tn-num" style={{
          fontFamily: 'Montserrat', fontWeight: 700, fontSize: 'clamp(42px, 4.6vw, 56px)', lineHeight: 1, letterSpacing: '-0.02em',
        }}>
          {prefix}{n.toLocaleString()}{suffix}
        </div>
        <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 15, color: T.white, marginTop: 22 }}>{sub}</div>
        <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 13, color: T.ink2, marginTop: 8, lineHeight: 1.6 }}>{detail}</div>
      </div>
    </div>
  );
}

function CostSection({ onSeen }) {
  const metrics = [
    { prefix: '', val: 10,   suffix: ' hrs',  sub: 'a week on admin',       detail: 'Quoting, retyping, chasing, rebuilding the list.' },
    { prefix: '$', val: 8200, suffix: '',      sub: 'on one cold quote',     detail: `Cole's. Sent five days ago. Never opened. Nobody chased it.` },
    { prefix: '', val: 5,    suffix: ':00 AM', sub: 'start, most days',      detail: `The only quiet hour you get. It shouldn't have to be that one.` },
    { prefix: '', val: 114,  suffix: '',       sub: 'quotes lost',           detail: 'Some were never landing. Some just went quiet.' },
  ];
  return (
    <Section id="s-cost" sectionKey="cost" index={2} onSeen={onSeen} style={{ background: T.bg0 }}>
      <SectionHead eyebrow="THE COST" title="What that costs you"
        sub="Rough numbers, from what you told us. Not scary — just untidy." />
      <div style={{ maxWidth: 1240, margin: '48px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 22 }}>
        {metrics.map((m, i) => <MetricCard key={i} {...m} delay={i} />)}
      </div>
      <div style={{ maxWidth: 1240, margin: '44px auto 0' }}>
        <RevealItem>
          <div style={{
            padding: '22px 28px', border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.gold}`,
            background: T.bg1, borderRadius: 4,
          }}>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 15, color: T.white, lineHeight: 1.65 }}>
              None of this is a Xero problem. Xero is doing its job. The gap is everything that happens before the invoice &mdash; and after the handshake.
            </div>
          </div>
        </RevealItem>
      </div>
    </Section>
  );
}

function BuildSection({ onSeen }) {
  const items = [
    { t: 'Quoting that ends itself', b: 'You price it, they click Accept, the draft invoice lands in Xero. No PDF ping-pong. No retyping.' },
    { t: 'The list, but it carries itself', b: 'Your weekly list becomes a board. Nothing gets pasted. Nothing falls off.' },
    { t: 'Everyone has a login', b: 'You see everything. Scott, Kai, Kate and Ty see their own jobs, on their phone, in the shed.' },
    { t: 'An assistant that reads it all', b: 'Quotes, jobs, Xero, mail, the calendar. It drafts, flags and nudges — and never sends without you.' },
  ];
  return (
    <Section id="s-build" sectionKey="build" index={3} onSeen={onSeen}>
      <SectionHead eyebrow="THE BUILD" title="One screen. Your shop on it."
        sub="An app that installs on the desktop and the phone. Every tile drags, resizes, or gets binned — and binning never deletes the data." />
      <div style={{ maxWidth: 1240, margin: '52px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 22 }}>
        {items.map((it, i) => (
          <RevealItem key={i} className="lift" delay={i + 1}>
            <div className="p2tn-card" style={{ padding: '30px 28px', minHeight: 210 }}>
              <div style={{ width: 36, height: 36, border: `1.5px solid ${T.gold}`,
                display: 'grid', placeItems: 'center', color: T.gold, fontFamily: 'Montserrat',
                fontWeight: 700, fontSize: 14, marginBottom: 20, borderRadius: 4 }}>
                {String(i + 1).padStart(2, '0')}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 20, color: T.white, letterSpacing: '-0.01em', lineHeight: 1.22 }}>{it.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 12, lineHeight: 1.6 }}>{it.b}</div>
            </div>
          </RevealItem>
        ))}
      </div>
      <div style={{ maxWidth: 1240, margin: '44px auto 0' }}>
        <RevealItem>
          <div style={{ padding: '20px 26px', background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 4 }}>
            <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>NOTHING GETS REPLACED</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 8, lineHeight: 1.6 }}>
              Xero stays your books. The NAS stays your drives. We stop the double-handling &mdash; we don&rsquo;t rip anything out.
            </div>
          </div>
        </RevealItem>
      </div>
    </Section>
  );
}

// Generic "mockup showcase" section — full-content-width cropped PDF image
// (stacked: image on top, bullets below in a grid). Click image → lightbox.
function MockupSection({ onSeen, sectionKey, index, eyebrow, title, sub, bullets, image, imageFull, alt, id, onEnlarge }) {
  return (
    <Section id={id} sectionKey={sectionKey} index={index} onSeen={onSeen}>
      <SectionHead eyebrow={eyebrow} title={title} sub={sub} />
      <div style={{ maxWidth: 1400, margin: '50px auto 0' }}>
        <RevealItem className="lift">
          <button
            type="button"
            data-testid={`mockup-enlarge-${sectionKey}`}
            onClick={() => onEnlarge({ src: imageFull || image, alt })}
            className="p2tn-mockup-btn"
            aria-label={`Enlarge ${alt}`}
            style={{
              display: 'block', width: '100%', padding: 0, background: T.bg1,
              border: `1px solid ${T.border}`, borderRadius: 4, cursor: 'zoom-in',
              boxShadow: '0 40px 100px rgba(0,0,0,0.5)', position: 'relative', overflow: 'hidden',
            }}
          >
            <img src={image} alt={alt} loading="lazy"
              style={{ display: 'block', width: '100%', height: 'auto' }}
            />
            <span aria-hidden className="p2tn-enlarge-hint">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6" /><path d="M9 21H3v-6" /><path d="M21 3l-7 7" /><path d="M3 21l7-7" />
              </svg>
              <span>Tap to enlarge</span>
            </span>
          </button>
        </RevealItem>
      </div>
      {bullets && bullets.length > 0 && (
        <div style={{ maxWidth: 1240, margin: '48px auto 0', display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 26 }}>
          {bullets.map((b, i) => (
            <RevealItem key={i} delay={i + 1}>
              <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>
                <span className="p2tn-rule" />{b.tag || String(i + 1).padStart(2, '0')}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 18, color: T.white, marginTop: 12, letterSpacing: '-0.01em' }}>{b.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 8, lineHeight: 1.6 }}>{b.b}</div>
            </RevealItem>
          ))}
        </div>
      )}
    </Section>
  );
}

function FlowSection({ onSeen }) {
  const steps = [
    { n: '01', by: 'YOU',    t: 'Enquiry lands',  b: 'Email or a call. The assistant reads it, finds the client in Xero, and opens a draft quote with the lines it can already see.' },
    { n: '02', by: 'YOU',    t: 'You quote it',   b: 'Line items, price, drawing from the NAS. AI flags the margin against past jobs. One click sends it.' },
    { n: '03', by: 'THEM',   t: 'They accept',    b: 'A branded 2TN quote page. They click Accept. No chain of emails, no PDF ping-pong.' },
    { n: '04', by: 'NOBODY', t: 'Xero and the floor', b: 'Draft invoice in Xero. Job on the board. Tasks to Scott, Kai and Kate. Automatically.' },
  ];
  const notes = [
    ['Nothing gets typed twice', 'The quote becomes the invoice becomes the job. One set of numbers, all the way through.'],
    ['The assistant never sends', 'It drafts, flags and nudges. Every send, every price, every invoice waits on your click.'],
    ['Built on the habit you already have', `Your weekly list becomes the board. Same habit — it just stops falling on the floor.`],
  ];
  return (
    <Section id="s-flow" sectionKey="flow" index={7} onSeen={onSeen}>
      <SectionHead eyebrow="THE FLOW" title="Enquiry to invoice, without typing it twice"
        sub="Four steps. Two of them happen without you." />
      <div style={{ maxWidth: 1240, margin: '52px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
        {steps.map((s, i) => (
          <RevealItem key={s.n} delay={i + 1}>
            <div className="p2tn-card" style={{ padding: '28px 26px', minHeight: 240, position: 'relative' }}>
              <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>
                <span className="p2tn-rule" />{s.n}
              </div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 20, color: T.white, marginTop: 14, letterSpacing: '-0.01em' }}>{s.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 13.5, color: T.ink2, marginTop: 10, lineHeight: 1.6 }}>{s.b}</div>
              <div style={{ position: 'absolute', bottom: 18, left: 26, fontFamily: 'Montserrat', fontWeight: 700, fontSize: 10, letterSpacing: '0.22em', color: T.gold }}>
                &rarr; {s.by}
              </div>
            </div>
          </RevealItem>
        ))}
      </div>
      <div style={{ maxWidth: 1240, margin: '44px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 26 }}>
        {notes.map(([t, b], i) => (
          <RevealItem key={i} delay={i + 1}>
            <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>· {String(i + 1).padStart(2, '0')}</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 17, color: T.white, marginTop: 8 }}>{t}</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 6, lineHeight: 1.6 }}>{b}</div>
          </RevealItem>
        ))}
      </div>
    </Section>
  );
}

function AssistantSection({ onSeen }) {
  const boxes = [
    { t: 'It catches the cold quote',    b: `"Cole's has sat five days unopened. I've drafted a nudge — warm, not pushy. Want to read it?"` },
    { t: 'It sees the bottleneck first', b: `"Scott's at 90% and ServerX starts tomorrow. Kate has room — move the Base Training benches?"` },
    { t: 'It watches the money',         b: `"MDI accepted yesterday. The draft invoice is still sitting unapproved in Xero — $5,400."` },
    { t: `It tells you what's working`,  b: `"Win rate's 67%, up from 58%. Quotes you send inside 24 hours close twice as often."` },
  ];
  return (
    <Section id="s-assistant" sectionKey="assistant" index={11} onSeen={onSeen}>
      <SectionHead eyebrow="THE ASSISTANT" title={<>It reads everything.<br /><span className="gold-text">It never sends anything.</span></>}
        sub={`It sits across the quotes, the jobs, Xero, your mail and your calendar. It's the only thing in the shop that sees all of it at once.`} />
      <div style={{ maxWidth: 1240, margin: '52px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 22 }}>
        {boxes.map((b, i) => (
          <RevealItem key={i} className="lift" delay={i + 1}>
            <div className="p2tn-card" style={{ padding: '28px 28px', minHeight: 180, borderLeft: `3px solid ${T.gold}` }}>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 19, color: T.white, letterSpacing: '-0.01em' }}>{b.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 12, lineHeight: 1.6 }}>{b.b}</div>
            </div>
          </RevealItem>
        ))}
      </div>
      <div style={{ maxWidth: 1240, margin: '44px auto 0' }}>
        <RevealItem>
          <div style={{ padding: '28px 30px', background: '#000', border: `1px solid ${T.gold}`, borderRadius: 4, position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, top: 0, width: 5, height: '100%', background: T.gold }} />
            <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>THE HARD RULE</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 17, color: T.white, marginTop: 10, lineHeight: 1.55, maxWidth: 900 }}>
              It drafts. It flags. It nudges. It never sends an email, never sets a price, never approves an invoice. Every one of those waits on you.
            </div>
          </div>
        </RevealItem>
      </div>
    </Section>
  );
}

function WhatWedBuildSection({ onSeen }) {
  const items = [
    { n: '01', t: 'One place to see everything', b: `The shop lives in three places — a weekly list, Xero, and your head. One screen instead: every live job, every quote, what the crew's on, what you're owed. The day starts with a look, not an hour of reassembly.` },
    { n: '02', t: 'Quoting, end to end', b: 'You price it once. The client gets a 2TN quote page — your brand, one Accept button. You know the second they open it. If it goes quiet, the assistant drafts the nudge.' },
    { n: '03', t: 'Accept straight into Xero', b: 'They click Accept, a draft invoice appears in Xero with the same lines you quoted. You review, approve, send. Xero stays your books — it just stops being data entry.' },
    { n: '04', t: 'The board, and a dashboard each', b: 'Your weekly list becomes a board that carries itself. Scott, Kai, Kate and Ty each get their own dashboard — their jobs, their tasks, their drawings. They tick it, you see it.' },
    { n: '05', t: 'The 2TN Assistant', b: `Across all of it. Nudges the cold quote, flags Scott before he's underwater, tells you the invoice is sitting unapproved. It drafts, flags and nudges — it never sends without you.` },
  ];
  return (
    <Section id="s-what-wed-build" sectionKey="what_wed_build" index={12} onSeen={onSeen}>
      <div style={{ maxWidth: 1240, margin: '0 auto' }}>
        <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11, marginBottom: 12 }}>
          FOR 2TN FABRICATION &nbsp;·&nbsp; DANIEL VISSER
        </div>
      </div>
      <SectionHead eyebrow="THE BUILD" title="What we'd build"
        sub="Five things. Nothing you wouldn't use on Monday." />
      <div style={{ maxWidth: 1100, margin: '46px auto 0' }}>
        {items.map((it, i) => (
          <RevealItem key={it.n} className="slide" delay={(i % 5) + 1}>
            <div style={{
              display: 'grid', gridTemplateColumns: '96px 1fr', gap: 34,
              padding: '28px 0', borderTop: `1px solid ${T.border}`,
            }}>
              <div className="gold-text" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 44, lineHeight: 1 }}>{it.n}</div>
              <div>
                <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 22, color: T.white, letterSpacing: '-0.01em' }}>{it.t}</div>
                <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 15, color: T.ink2, marginTop: 10, lineHeight: 1.65 }}>{it.b}</div>
              </div>
            </div>
          </RevealItem>
        ))}
      </div>
      <div style={{ maxWidth: 1100, margin: '38px auto 0' }}>
        <RevealItem>
          <div style={{ padding: '22px 28px', background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 4 }}>
            <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>HOW WE WORK</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 15, color: T.ink2, marginTop: 8, lineHeight: 1.65 }}>
              What we build, you own. No subscription for it. Xero stays yours, the NAS stays yours &mdash; we connect what you already use rather than replace it.
            </div>
          </div>
        </RevealItem>
      </div>
    </Section>
  );
}

function TimelineSection({ onSeen }) {
  const weeks = [
    { w: 'WEEK 1–2', t: 'The screen',   b: `Your dashboard, your tiles, everyone's logins. Xero connected and reading. The shop on one screen.` },
    { w: 'WEEK 2–4', t: 'The quote',    b: 'Quote builder, the client page, the Accept button, draft invoice into Xero. We run your held-back quote through it, live.' },
    { w: 'WEEK 4–5', t: 'The floor',    b: 'The board, and a dashboard each for Scott, Kai, Kate and Ty. On their phones, in the shed.' },
    { w: 'WEEK 5–6', t: 'The assistant', b: 'Nudges, margin checks, load flags — tuned on your real jobs, not a demo.' },
  ];
  return (
    <Section id="s-timeline" sectionKey="timeline" index={13} onSeen={onSeen}>
      <SectionHead eyebrow="TIMELINE" title="Six weeks. Live on a real job."
        sub={`You've got a quote request you deliberately held back, waiting for this. That's the test case.`} />
      <div style={{ maxWidth: 1240, margin: '52px auto 0', display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
        {weeks.map((w, i) => (
          <RevealItem key={w.w} className="lift" delay={i + 1}>
            <div className="p2tn-card" style={{ padding: '30px 26px', minHeight: 220 }}>
              <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>{w.w}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 22, color: T.white, marginTop: 14, letterSpacing: '-0.01em' }}>{w.t}</div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 10, lineHeight: 1.6 }}>{w.b}</div>
            </div>
          </RevealItem>
        ))}
      </div>
      <div style={{ maxWidth: 1240, margin: '38px auto 0' }}>
        <RevealItem>
          <div style={{ padding: '26px 30px', background: '#000', border: `1px solid ${T.gold}`, borderRadius: 4 }}>
            <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>THE TEST CASE</div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 17, color: T.white, marginTop: 10, lineHeight: 1.55, maxWidth: 940 }}>
              The quote you held back goes through the system end to end &mdash; priced, sent, accepted, into Xero, onto the floor &mdash; before anything else goes live.
            </div>
          </div>
        </RevealItem>
      </div>
    </Section>
  );
}

function CTASection({ onSeen, onCta, ctaState }) {
  const r = useReveal();
  return (
    <Section id="s-cta" sectionKey="cta" index={14} onSeen={onSeen}
      style={{ paddingBottom: 'clamp(120px, 14vw, 200px)', minHeight: '72vh', display: 'flex', alignItems: 'center' }}>
      <img src={`${ASSETS}/blade-hero.png`} alt="" aria-hidden
        style={{ position: 'absolute', right: 0, bottom: 0, height: '90%', maxWidth: '42vw',
                 objectFit: 'contain', objectPosition: 'right bottom', opacity: 0.85, pointerEvents: 'none' }}
      />
      <div ref={r.ref} className={`p2tn-reveal ${r.cls}`} style={{ maxWidth: 1240, margin: '0 auto', width: '100%', position: 'relative' }}>
        <img src={`${ASSETS}/logo-header.png`} alt="2TN Fabrication" style={{ height: 60, display: 'block', marginBottom: 30 }} />
        <div className="p2tn-eyebrow gold-text" style={{ fontSize: 12, marginBottom: 20 }}>SECOND TO NONE</div>
        <h2 style={{
          fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.03em',
          fontSize: 'clamp(52px, 7.5vw, 100px)', lineHeight: 1.02, color: T.white,
        }}>
          Get your mornings back.
        </h2>
        <p style={{
          fontFamily: 'Montserrat', fontWeight: 400, fontSize: 'clamp(16px, 1.5vw, 19px)',
          color: T.ink2, marginTop: 30, maxWidth: 700, lineHeight: 1.65,
        }}>
          You built a fabrication shop in a year on word of mouth alone. You shouldn&rsquo;t have to start it at five in the morning.
        </p>
        <div style={{ marginTop: 52, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <button data-testid="pitch-cta-lets-build" className="p2tn-btn primary" onClick={() => onCta('lets_build')}>
            {ctaState === 'lets_build_sent' ? '✓ SENT TO MICHAEL' : "LET'S BUILD IT →"}
          </button>
          <button data-testid="pitch-cta-questions" className="p2tn-btn ghost" onClick={() => onCta('questions_first')}>
            {ctaState === 'questions_first_sent' ? '✓ WE WILL BE IN TOUCH' : 'QUESTIONS FIRST'}
          </button>
        </div>
        <div style={{
          marginTop: 96, borderTop: `1px solid ${T.border}`, paddingTop: 26,
          display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', alignItems: 'baseline',
        }}>
          <div className="p2tn-eyebrow" style={{ color: T.ink3, fontSize: 11 }}>2TN FABRICATION &nbsp;·&nbsp; JULY 2026</div>
          <div className="p2tn-eyebrow" style={{ color: T.ink3, fontSize: 11 }}>
            KINDRA AI &nbsp;·&nbsp; BRISBANE &nbsp;·&nbsp; <span className="gold-text">michael@kindraai.dev</span>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// KINDRA BOOKENDS — frame around the 2TN experience
// ─────────────────────────────────────────────────────────────────────

function KindraIntroSection({ onSeen, prepared_for }) {
  const ref = useSectionTracker('intro', -1, onSeen);
  const r = useReveal();
  // First names only, in the same friendly tone as the deck
  const names = (prepared_for || 'Daniel Visser').split(' ')[0];
  return (
    <section ref={ref} data-section="intro" className="k-section" style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      padding: 'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 96px)',
    }}>
      <div className="k-aurora" aria-hidden>
        <div className="k-aurora-a" /><div className="k-aurora-b" /><div className="k-aurora-c" />
      </div>
      <div ref={r.ref} className={`p2tn-reveal ${r.cls}`} style={{ maxWidth: 900, margin: '0 auto', width: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <img src="/brand/kindra-logo-dark.svg" alt="Kindra AI" style={{ height: 44, display: 'block' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>

        <div style={{ marginTop: 60, display: 'flex', alignItems: 'center', gap: 20 }}>
          <img
            src="/showcase/michael-avatar.jpg"
            alt="Michael, founder of Kindra AI"
            style={{
              width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
              border: `2px solid transparent`, background: `${SPECTRUM} border-box`,
              boxShadow: '0 8px 22px rgba(145,86,196,0.35)',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 13, color: K.icyMute, letterSpacing: '0.02em' }}>
            From Michael &middot; Kindra AI &middot; Brisbane
          </div>
        </div>

        <h1 style={{
          fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.03em',
          fontSize: 'clamp(48px, 7.4vw, 92px)', lineHeight: 1.02,
          color: K.icy, marginTop: 40,
        }}>
          {names}, Scott &mdash; <span className="k-spectrum-text">welcome.</span>
        </h1>

        <div style={{
          marginTop: 40, fontFamily: 'Montserrat', fontWeight: 400,
          fontSize: 'clamp(16px, 1.5vw, 19px)', color: K.icy, lineHeight: 1.7, maxWidth: 720,
        }}>
          <p style={{ margin: 0 }}>
            We&rsquo;ve spent time understanding how 2TN runs &mdash; the mornings, the quotes, the list, the crew.
          </p>
          <p style={{ margin: '18px 0 0' }}>
            What follows is what we found, what it&rsquo;s costing you, and what we&rsquo;d build &mdash; presented in your colours,
            because it&rsquo;s your business we&rsquo;re talking about.
          </p>
          <p style={{ margin: '18px 0 0', color: K.icyMute }}>
            Take your time with it. Scroll, poke around, tap anything you want to see closer. When you&rsquo;re done, there&rsquo;s a
            button at the end &mdash; or my email is right here whenever you&rsquo;re ready.
          </p>
        </div>

        <div style={{ marginTop: 44, display: 'flex', alignItems: 'center', gap: 20 }}>
          <span className="k-spectrum-bar" />
          <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 13, color: K.icy, letterSpacing: '0.01em' }}>
            &mdash; Michael, Kindra AI
          </div>
        </div>

        <div className="k-scroll-cue" style={{ marginTop: 84, display: 'flex', alignItems: 'center', gap: 12, color: K.icyDim }}>
          <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase' }}>
            The 2TN Operations OS
          </span>
          <svg width="16" height="20" viewBox="0 0 16 20" fill="none" aria-hidden>
            <path d="M8 2 L8 16 M2 10 L8 16 L14 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </section>
  );
}

function KindraTransition() {
  return <div className="k-to-t2n" aria-hidden />;
}
function TwoTNToKindraTransition() {
  return <div className="t2n-to-k" aria-hidden />;
}

function KindraEndSection({ onSeen, onCta, ctaState }) {
  const ref = useSectionTracker('end', 15, onSeen);
  const r = useReveal();
  return (
    <section ref={ref} data-section="end" className="k-section" style={{
      padding: 'clamp(90px, 10vw, 140px) clamp(24px, 6vw, 96px) clamp(60px, 7vw, 96px)',
      minHeight: '80vh', display: 'flex', alignItems: 'center',
    }}>
      <div className="k-aurora" aria-hidden>
        <div className="k-aurora-a" style={{ opacity: 0.28 }} />
        <div className="k-aurora-b" style={{ opacity: 0.22 }} />
      </div>

      <div ref={r.ref} className={`p2tn-reveal ${r.cls}`} style={{ maxWidth: 1100, margin: '0 auto', width: '100%', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <img src="/brand/kindra-logo-dark.svg" alt="Kindra AI" style={{ height: 40, display: 'block' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>

        <div style={{ marginTop: 44, fontFamily: 'Montserrat', fontWeight: 600, fontSize: 12, letterSpacing: '0.24em', textTransform: 'uppercase', color: K.icyDim }}>
          Built by Kindra AI &middot; Brisbane
        </div>

        <h2 style={{
          fontFamily: 'Montserrat', fontWeight: 800, letterSpacing: '-0.02em',
          fontSize: 'clamp(40px, 5.4vw, 68px)', lineHeight: 1.08,
          color: K.icy, marginTop: 20,
        }}>
          Find your leaks. <span className="k-spectrum-text">Plug your leaks.</span>
        </h2>

        <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 40, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <img
              src="/showcase/michael-avatar.jpg"
              alt="Michael, founder of Kindra AI"
              style={{
                width: 72, height: 72, borderRadius: '50%', objectFit: 'cover',
                boxShadow: '0 12px 30px rgba(145,86,196,0.35)',
              }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 17, color: K.icy, letterSpacing: '-0.01em' }}>
                You&rsquo;ll talk to Michael.
              </div>
              <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: K.icyMute, marginTop: 6, lineHeight: 1.55, maxWidth: 340 }}>
                No sales team &mdash; just the bloke who&rsquo;ll build it. If we start, it&rsquo;s me on the tools.
              </div>
            </div>
          </div>

          <div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 15, color: K.icyMute, lineHeight: 1.7 }}>
              The 2TN deck has done the selling. This page is Kindra signing the work &mdash; a promise, one contact,
              and a way in whenever you&rsquo;re ready.
            </div>
            <div style={{ marginTop: 20, fontFamily: 'Montserrat', fontWeight: 600, fontSize: 14, color: K.icy }}>
              <a href="mailto:michael@kindraai.dev"
                className="k-spectrum-text"
                style={{ textDecoration: 'none', fontWeight: 700, letterSpacing: '0.01em' }}>
                michael@kindraai.dev
              </a>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 60, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <button data-testid="kindra-cta-lets-build" className="k-cta primary" onClick={() => onCta('lets_build')}>
            {ctaState === 'lets_build_sent' ? '✓ SENT TO MICHAEL' : "LET'S BUILD IT →"}
          </button>
          <button data-testid="kindra-cta-questions" className="k-cta ghost" onClick={() => onCta('questions_first')}>
            {ctaState === 'questions_first_sent' ? '✓ WE WILL BE IN TOUCH' : 'QUESTIONS FIRST'}
          </button>
        </div>

        <div style={{ marginTop: 80 }}>
          <span className="k-hairline" />
          <div style={{ marginTop: 22, display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', color: K.icyDim }}>
              Kindra AI &middot; Brisbane, QLD
            </div>
            <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 11, letterSpacing: '0.20em', textTransform: 'uppercase', color: K.icyDim }}>
              Prepared for 2TN Fabrication &middot; July 2026
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Lightbox (click-to-enlarge on every mockup) ──────────────────────
function Lightbox({ item, onClose }) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [item, onClose]);
  if (!item) return null;
  return (
    <div className="p2tn p2tn-lb-overlay" data-testid="pitch-lightbox" onClick={onClose}>
      <div className="p2tn-lb-bar" onClick={(e) => e.stopPropagation()}>
        <span className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>2TN OPERATIONS OS · {item.alt}</span>
        <button className="p2tn-lb-close" data-testid="pitch-lightbox-close" aria-label="Close" onClick={onClose}>×</button>
      </div>
      <div className="p2tn-lb-scroll" onClick={(e) => e.stopPropagation()}>
        <img src={item.src} alt={item.alt} />
      </div>
      <div className="p2tn-lb-bar" style={{ borderTop: `1px solid ${T.border}`, borderBottom: 'none', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
        <span className="p2tn-eyebrow" style={{ color: T.ink3, fontSize: 10 }}>PINCH TO ZOOM · TAP OUTSIDE OR ESC TO CLOSE</span>
      </div>
    </div>
  );
}

// ── CTA Modal ────────────────────────────────────────────────────────
function CTAModal({ open, cta, onClose, onSubmit }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setName(''); setEmail(''); setNote(''); setBusy(false); } }, [open]);
  if (!open) return null;
  const title = cta === 'lets_build' ? `Let's build it` : 'Questions first';
  const sub = cta === 'lets_build'
    ? 'Great. Drop a note and Michael will be in touch inside the day.'
    : `Anything on your mind — fire it through.`;
  return (
    <div className="p2tn" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
      zIndex: 90, display: 'grid', placeItems: 'center', padding: 20,
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.bg2, border: `1px solid ${T.gold}`, maxWidth: 520, width: '100%', padding: 30, borderRadius: 6,
      }}>
        <div className="p2tn-eyebrow gold-text" style={{ fontSize: 11 }}>
          {cta === 'lets_build' ? "LET'S BUILD" : 'QUESTIONS FIRST'}
        </div>
        <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 24, color: T.white, marginTop: 8 }}>{title}</div>
        <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 8, lineHeight: 1.55 }}>{sub}</div>
        <div style={{ marginTop: 20, display: 'grid', gap: 12 }}>
          <input data-testid="pitch-modal-name"  value={name}  onChange={(e) => setName(e.target.value)}  placeholder="Your name" style={INP} />
          <input data-testid="pitch-modal-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (so we can reply)" style={INP} />
          <textarea data-testid="pitch-modal-note" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Anything you'd like Michael to know…" rows={4} style={{ ...INP, resize: 'vertical' }} />
        </div>
        <div style={{ marginTop: 22, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="p2tn-btn ghost">CANCEL</button>
          <button data-testid="pitch-modal-send" disabled={busy} className="p2tn-btn primary"
            onClick={async () => { setBusy(true); const ok = await onSubmit({ name, email, note }); setBusy(false); if (ok) onClose(); }}>
            {busy ? 'SENDING…' : 'SEND TO MICHAEL'}
          </button>
        </div>
      </div>
    </div>
  );
}

const INP = {
  background: T.bg0, border: `1px solid ${T.border}`, color: T.white,
  padding: '12px 14px', fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, borderRadius: 4, outline: 'none',
};

// ─────────────────────────────────────────────────────────────────────
// PAGE SHELL
// ─────────────────────────────────────────────────────────────────────
export default function V2PitchPage() {
  const { token } = useParams();
  const [pitch, setPitch] = useState(null);
  const [err, setErr] = useState(null);
  const [progress, setProgress] = useState(0);
  const [modal, setModal] = useState(null);
  const [ctaState, setCtaState] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const seenSections = useRef(new Set());

  useEffect(() => {
    document.title = '2TN Operations OS — Prepared for Daniel Visser';
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex,nofollow,noarchive';
    document.head.appendChild(m);
    return () => { try { document.head.removeChild(m); } catch { /* noop */ } };
  }, []);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pitch/${token}`);
        if (!r.ok) { setErr(r.status === 404 ? 'not_found' : 'error'); return; }
        setPitch(await r.json());
      } catch { setErr('error'); }
    })();
  }, [token]);

  const reportSection = useMemo(() => (key, index) => {
    if (!token || seenSections.current.has(key)) return;
    seenSections.current.add(key);
    fetch(`${API}/api/pitch/${token}/event`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'section', section: key, index }),
    }).catch(() => { /* noop */ });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const int = setInterval(() => {
      if (document.hidden) return;
      fetch(`${API}/api/pitch/${token}/event`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: 'heartbeat', seconds: 20 }),
      }).catch(() => { /* noop */ });
    }, 20000);
    return () => clearInterval(int);
  }, [token]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const h = doc.scrollHeight - doc.clientHeight;
      const p = h > 0 ? doc.scrollTop / h : 0;
      setProgress(Math.min(1, Math.max(0, p)));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openCta = (cta) => {
    if (!token) return;
    fetch(`${API}/api/pitch/${token}/event`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'cta', cta }),
    }).catch(() => { /* noop */ });
    setModal(cta);
  };

  const submitCta = async ({ name, email, note }) => {
    if (!modal) return false;
    try {
      const r = await fetch(`${API}/api/pitch/${token}/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cta: modal, name, email, note }),
      });
      if (r.ok) { setCtaState(`${modal}_sent`); return true; }
    } catch { /* noop */ }
    return false;
  };

  if (err === 'not_found') {
    return (
      <div className="p2tn" style={{ minHeight: '100vh', background: T.bg0, display: 'grid', placeItems: 'center', padding: 40 }}>
        <PageStyle />
        <div style={{ textAlign: 'center', maxWidth: 460 }}>
          <img src={`${ASSETS}/logo-header.png`} alt="2TN Fabrication" style={{ height: 56 }} />
          <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 30, color: T.white, marginTop: 24 }}>
            This pitch isn&rsquo;t live.
          </div>
          <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: T.ink2, marginTop: 12, lineHeight: 1.55 }}>
            The link may have expired or been revoked. If you were expecting to see something here, drop Michael a note:{' '}
            <span className="gold-text">michael@kindraai.dev</span>.
          </div>
        </div>
      </div>
    );
  }
  if (!pitch) {
    return (
      <div className="p2tn" style={{ minHeight: '100vh', background: T.bg0, display: 'grid', placeItems: 'center' }}>
        <PageStyle />
        <div className="p2tn-eyebrow gold-text" style={{ fontSize: 12, opacity: 0.7 }}>LOADING…</div>
      </div>
    );
  }

  // Template dispatcher — the /pitch/:token route serves multiple client
  // experiences. Add new templates alongside V2AchPitchPage.
  if (pitch.template === 'ach_hire_system' || pitch.template === 'base_training_gym_os' || pitch.template === 'hpci_interiors_os') {
    const onEvent = (payload) => {
      if (payload.kind === 'section') {
        if (seenSections.current.has(payload.section)) return;
        seenSections.current.add(payload.section);
      }
      fetch(`${API}/api/pitch/${token}/event`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => { /* noop */ });
    };
    const onMessage = async ({ cta, name, email, note }) => {
      try {
        const r = await fetch(`${API}/api/pitch/${token}/message`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cta, name, email, note }),
        });
        return r.ok;
      } catch { return false; }
    };
    if (pitch.template === 'base_training_gym_os') {
      return <V2BasePitchPage pitch={pitch} onEvent={onEvent} onMessage={onMessage} />;
    }
    if (pitch.template === 'hpci_interiors_os') {
      return <V2HPCIPitchPage pitch={pitch} onEvent={onEvent} onMessage={onMessage} />;
    }
    return <V2AchPitchPage pitch={pitch} onEvent={onEvent} onMessage={onMessage} />;
  }

  return (
    <div className="p2tn" style={{ background: T.bg0, minHeight: '100vh' }}>
      <PageStyle />
      <div className="p2tn-progress" style={{ width: `${progress * 100}%` }} />

      <KindraIntroSection onSeen={reportSection} prepared_for={pitch.prepared_for} />
      <KindraTransition />

      <CoverSection onSeen={reportSection} prepared_for={pitch.prepared_for} business_name={pitch.business_name} />
      <LeaksSection onSeen={reportSection} />
      <CostSection onSeen={reportSection} />
      <BuildSection onSeen={reportSection} />

      <MockupSection
        id="s-floor" sectionKey="floor" index={4} onSeen={reportSection}
        eyebrow="YOUR MORNING · THE FLOOR" title="Everything, in one glance"
        image={`${ASSETS}/mockup-floor@2x.png`} imageFull={`${ASSETS}/mockup-floor.png`}
        alt="The Floor — dashboard mockup" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'Now — what needs you', b: `The one thing costing you money right now. Today: Cole's cold quote.` },
          { tag: '02', t: 'Projects', b: `ServerX, MDI, Base Training, GT, Gillam. Progress, value, who's on it, when it's due.` },
          { tag: '03', t: 'Crew load', b: `Scott's at 90%. Kate has room. You see it before Scott has to tell you.` },
          { tag: '04', t: 'Money — Xero', b: 'Owed, invoiced, awaiting your approval. Live from Xero, no export.' },
        ]}
      />

      <MockupSection
        id="s-quoting" sectionKey="quoting" index={5} onSeen={reportSection}
        eyebrow="QUOTING · YOUR SIDE" title="Price it once. Send it once."
        image={`${ASSETS}/mockup-quoting@2x.png`} imageFull={`${ASSETS}/mockup-quoting.png`}
        alt="Quote builder mockup" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'Client pulled from Xero', b: 'Stowe Australia. Contact, terms, payment history — already there.' },
          { tag: '02', t: 'Line items, not retyping', b: 'You build once. The client, the invoice and the job all read from the same lines.' },
          { tag: '03', t: 'AI checks your margin', b: `"Last three shelf jobs averaged $412/unit. You're at $370." It asks. You decide.` },
          { tag: '04', t: 'Drawings from the NAS', b: 'shelf-rev-B.pdf attached without you going and finding it.' },
        ]}
      />

      <MockupSection
        id="s-client-quote" sectionKey="client_quote" index={6} onSeen={reportSection}
        eyebrow="THE CLIENT QUOTE · WHAT STOWE OPENS" title={<>They don&rsquo;t get a PDF. <span className="gold-text">They get 2TN.</span></>}
        sub="Right now a quote leaves as an attachment and lands in a pile. This lands as a page with your name on it — and one button."
        image={`${ASSETS}/mockup-client-quote@2x.png`} imageFull={`${ASSETS}/mockup-client-quote.png`}
        alt="Client quote page mockup" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'Your brand, not an attachment', b: 'Black, gold, your mark, the drawing attached. It looks like 2TN made it, because 2TN did.' },
          { tag: '02', t: `One button. That's the decision.`, b: `Accept quote. No print, no sign, no scan, no email back. Stowe clicks once and it's done.` },
          { tag: '03', t: 'You know the second they open it', b: `Opened three times? That's hot. Not opened in five days? The assistant has the nudge drafted.` },
          { tag: '04', t: 'Accepting does the paperwork', b: 'Draft invoice into Xero. Job onto the board. Tasks to Scott and Kate. Nobody types a thing.' },
        ]}
      />

      <FlowSection onSeen={reportSection} />

      <MockupSection
        id="s-board" sectionKey="board" index={8} onSeen={reportSection}
        eyebrow="JOBS · THE BOARD" title="The list, but it carries itself"
        image={`${ASSETS}/mockup-board@2x.png`} imageFull={`${ASSETS}/mockup-board.png`}
        alt="Kanban board mockup" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'Six columns, no copy-paste', b: 'Quote sent, Accepted, Drafting, Fabrication, QA, Done. Drag it or let it move itself.' },
          { tag: '02', t: 'The job carries its own history', b: `ServerX still knows it's rev B, the PO number, and who touched it last.` },
          { tag: '03', t: 'Nothing falls off on a Friday', b: `Whatever isn't done rolls forward. It doesn't get deleted and forgotten.` },
        ]}
      />

      <MockupSection
        id="s-job" sectionKey="job" index={9} onSeen={reportSection}
        eyebrow="A JOB · SERVERX" title="Everything about one job"
        image={`${ASSETS}/mockup-job@2x.png`} imageFull={`${ASSETS}/mockup-job.png`}
        alt="Single job detail mockup" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'The stage bar', b: 'Quoted, accepted, drafting, fabrication. Where it actually is, not where you hoped.' },
          { tag: '02', t: 'Six tasks, four people', b: `Kate's drawings done. Scott's cutting. Kai's QA is booked. You've got the coater to ring.` },
          { tag: '03', t: 'The AI is watching the date', b: `"Powder coater needs four days. Book by Friday or you slip the 24th."` },
          { tag: '04', t: 'Xero, right there', b: 'Draft invoice INV-0012. Awaiting your approval. One click.' },
        ]}
      />

      <MockupSection
        id="s-crew" sectionKey="crew" index={10} onSeen={reportSection}
        eyebrow="THE CREW · SCOTT, KAI, KATE, TY" title="Same system. Their jobs."
        image={`${ASSETS}/mockup-crew@2x.png`} imageFull={`${ASSETS}/mockup-crew.png`}
        alt="Crew dashboard mockup — Scott" onEnlarge={setLightbox}
        bullets={[
          { tag: '01', t: 'Not a stripped-down version', b: 'Scott gets a real dashboard — his jobs, his tasks, his week, his drawings. Same tiles, his data.' },
          { tag: '02', t: 'He arranges it how he wants', b: 'Drag, resize, bin. His board is his. Yours is yours.' },
          { tag: '03', t: 'He ticks it, you see it', b: `No stand-up. No "where's that at?" It's already on your screen.` },
        ]}
      />

      <AssistantSection onSeen={reportSection} />
      <WhatWedBuildSection onSeen={reportSection} />
      <TimelineSection onSeen={reportSection} />
      <CTASection onSeen={reportSection} onCta={openCta} ctaState={ctaState} />
      <TwoTNToKindraTransition />
      <KindraEndSection onSeen={reportSection} onCta={openCta} ctaState={ctaState} />

      <CTAModal open={!!modal} cta={modal} onClose={() => setModal(null)} onSubmit={submitCta} />
      <Lightbox item={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
